"use client";

import { forwardRef } from "react";
import { Button as ButtonPrimitive } from "@base-ui/react/button";
import { cva, type VariantProps } from "class-variance-authority";
import { LoaderCircle } from "lucide-react";
import { cn } from "@/lib/utils";

const buttonVariants = cva(
  [
    "group/button inline-flex shrink-0 items-center justify-center gap-2",
    // Pill shape is canonical for Button. Previously Buttons rendered as
    // rounded-md (radius-md), while ad-hoc <Link>-as-button call sites were
    // already rounded-full — which made "Browse My Places" look pill while
    // "Run check" looked square. The app as a whole consistently uses pill
    // shape for pill-like tabs, badges, and branded CTAs, so align Button to
    // match. Icon and xs sizes override below where a smaller corner reads
    // better at that scale.
    "rounded-[var(--radius-full)] border border-transparent bg-clip-padding",
    "text-[length:var(--font-size-sm)] font-[number:var(--font-weight-medium)] whitespace-nowrap",
    "transition-all duration-150 ease-[cubic-bezier(0.4,0,0.2,1)]",
    "outline-none select-none",
    "active:scale-[0.97]",
    "focus-visible:border-[var(--color-border-focus)] focus-visible:ring-[3px] focus-visible:ring-[var(--color-primary-muted)]",
    "disabled:pointer-events-none disabled:opacity-50",
    "aria-invalid:border-[var(--color-error)] aria-invalid:ring-[3px] aria-invalid:ring-[var(--color-error-muted)]",
    "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  ].join(" "),
  {
    variants: {
      variant: {
        primary: [
          "bg-[var(--color-primary)] text-[var(--color-primary-foreground)]",
          "shadow-[0_1px_2px_rgba(0,0,0,0.1)]",
          "hover:bg-[var(--color-primary-hover)] hover:shadow-[0_2px_4px_rgba(0,0,0,0.15)]",
          "active:bg-[var(--color-primary-active)]",
        ].join(" "),
        secondary: [
          "border-[var(--color-border-default)] bg-[var(--color-bg-card)]",
          "text-[var(--color-text-primary)]",
          "hover:bg-[var(--color-bg-muted)]",
          "active:bg-[var(--color-border-default)]",
        ].join(" "),
        ghost: [
          "text-[var(--color-text-primary)]",
          "hover:bg-[var(--color-bg-muted)]",
          "active:bg-[var(--color-border-default)]",
        ].join(" "),
        destructive: [
          "bg-[var(--color-error-subtle)] text-[var(--color-error)]",
          "hover:bg-[var(--color-error-muted)]",
          "active:bg-[var(--color-error)]",
          "active:text-white",
          "focus-visible:border-[var(--color-error)] focus-visible:ring-[var(--color-error-muted)]",
        ].join(" "),
        link: [
          "text-[var(--color-text-link)] underline-offset-4",
          "hover:underline hover:text-[var(--color-text-link-hover)]",
        ].join(" "),
        // Keep backward compat aliases
        default: [
          "bg-[var(--color-primary)] text-[var(--color-primary-foreground)]",
          "hover:bg-[var(--color-primary-hover)]",
          "active:bg-[var(--color-primary-active)]",
        ].join(" "),
        outline: [
          "border-[var(--color-border-default)] bg-transparent",
          "text-[var(--color-text-primary)]",
          "hover:bg-[var(--color-bg-muted)]",
        ].join(" "),
      },
      size: {
        sm: "h-9 gap-1.5 px-4 text-[length:var(--font-size-xs)] [&_svg:not([class*='size-'])]:size-3.5",
        md: "h-10 gap-2 px-5",
        lg: "h-12 gap-2 px-6 text-[length:var(--font-size-base)]",
        // Icon buttons inherit the pill radius from the base — a circular
        // shape reads cleaner than a rounded square at icon dimensions.
        icon: "size-10 p-0",
        "icon-sm": "size-8 p-0 [&_svg:not([class*='size-'])]:size-3.5",
        "icon-lg": "size-12 p-0",
        // Keep backward compat
        default: "h-10 gap-2 px-5",
        xs: "h-7 gap-1 px-2 text-[length:var(--font-size-xs)] [&_svg:not([class*='size-'])]:size-3",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
    },
  }
);

interface ButtonProps
  extends Omit<React.ComponentProps<"button">, "disabled">,
    VariantProps<typeof buttonVariants> {
  /** Show a loading spinner and disable the button */
  loading?: boolean;
  /** Disable the button */
  disabled?: boolean;
  /**
   * Render the button as a different element (e.g. a Next.js Link).
   * The element's own props are preserved; button styling and children
   * are composed onto it so the resulting DOM is a single tag — not
   * a <button> wrapping an <a> or vice versa.
   */
  render?: React.ReactElement;
}

const Button = forwardRef<HTMLElement, ButtonProps>(
  (
    {
      className,
      variant = "primary",
      size = "md",
      loading = false,
      disabled = false,
      children,
      render,
      ...props
    },
    ref
  ) => {
    const content = (
      <>
        {loading ? (
          <LoaderCircle className="size-4 animate-spin" aria-hidden="true" />
        ) : null}
        {children}
      </>
    );

    return (
      <ButtonPrimitive
        ref={ref}
        data-slot="button"
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        className={cn(buttonVariants({ variant, size, className }))}
        {...(render ? { render, nativeButton: false } : {})}
        {...props}
      >
        {content}
      </ButtonPrimitive>
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
export type { ButtonProps };
